import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import Paginate from "./Paginate";
import { api } from "./api";

const currency = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });
const emptySummary = { totalInvoices: 0, invoiceValue: 0, amountReceived: 0, amountPending: 0 };

const CustomerTransactions = () => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(emptySummary);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [busyAction, setBusyAction] = useState("");
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [receiveData, setReceiveData] = useState({ amount: "", remark: "" });

  useEffect(() => {
    api.get("/api/credits/customers")
      .then((response) => setCustomers(response.data.data || []))
      .catch((error) => setErrorMessage(error.response?.data?.message || "Unable to load customers."))
      .finally(() => setCustomersLoading(false));
  }, []);

  const fetchTransactions = useCallback(async (customerId, requestedPage = 1) => {
    if (!customerId) {
      setTransactions([]); setSummary(emptySummary); setTotalPages(0); setPage(1);
      return;
    }
    setTransactionsLoading(true);
    setErrorMessage("");
    try {
      const response = await api.get(`/api/credits/transactions/${customerId}`, { params: { page: requestedPage, limit: 10 } });
      setTransactions(response.data.transactions || []);
      setSummary(response.data.summary || emptySummary);
      setPage(response.data.pagination.page);
      setTotalPages(response.data.pagination.totalPages);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Unable to load transactions.");
    } finally { setTransactionsLoading(false); }
  }, []);

  const changeCustomer = (event) => {
    const customerId = event.target.value;
    setSelectedCustomer(customerId);
    fetchTransactions(customerId, 1);
  };

  const generateTransactions = async () => {
    if (!selectedCustomer) return toast.warning("Select a customer first.");
    setBusyAction("generate");
    try {
      const response = await api.post("/api/credits/generate", { customerId: selectedCustomer });
      toast.success(response.data.message || "Transactions refreshed.");
      await fetchTransactions(selectedCustomer, 1);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to refresh transactions.");
    } finally { setBusyAction(""); }
  };

  const closeReceiveModal = () => {
    if (busyAction === "payment") return;
    setShowReceiveModal(false);
    setReceiveData({ amount: "", remark: "" });
  };

  const submitPayment = async (event) => {
    event.preventDefault();
    const amount = Number(receiveData.amount);
    if (!selectedCustomer) return toast.warning("Select a customer first.");
    if (!Number.isFinite(amount) || amount <= 0) return toast.warning("Enter a payment amount greater than zero.");
    if (amount > summary.amountPending) return toast.warning(`Payment cannot exceed ${currency.format(summary.amountPending)}.`);
    if (receiveData.remark.trim().length > 500) return toast.warning("Remark cannot exceed 500 characters.");
    if (!window.confirm(`Record a payment of ${currency.format(amount)}?`)) return;

    setBusyAction("payment");
    try {
      const response = await api.post("/api/credits/receive-payment", {
        customerId: selectedCustomer,
        amount,
        remark: receiveData.remark.trim(),
      });
      toast.success(response.data.message || "Payment received.");
      setShowReceiveModal(false);
      setReceiveData({ amount: "", remark: "" });
      await fetchTransactions(selectedCustomer, 1);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to receive payment.");
    } finally { setBusyAction(""); }
  };

  return <main className="container mt-4 mb-5">
    <div className="mb-3"><h1 className="h2 mb-1">Customer Credit</h1><p className="text-muted">Review customer balances and record payments.</p></div>
    {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
    <Row className="g-2 align-items-end mb-4">
      <Col lg={6}><Form.Group><Form.Label>Customer</Form.Label><Form.Select value={selectedCustomer} onChange={changeCustomer} disabled={customersLoading || Boolean(busyAction)}><option value="">{customersLoading ? "Loading customers..." : "Select customer"}</option>{customers.map((customer) => <option key={customer.person_id} value={customer.person_id}>{customer.first_name} {customer.last_name}</option>)}</Form.Select></Form.Group></Col>
      <Col sm={6} lg={3}><Button className="w-100" onClick={generateTransactions} disabled={!selectedCustomer || Boolean(busyAction)}>{busyAction === "generate" ? <><Spinner size="sm" className="me-2" />Refreshing...</> : "Generate & refresh"}</Button></Col>
      <Col sm={6} lg={3}><Button className="w-100" variant="success" onClick={() => setShowReceiveModal(true)} disabled={!selectedCustomer || summary.amountPending <= 0 || Boolean(busyAction)}>Receive payment</Button></Col>
    </Row>

    {selectedCustomer && <Row className="g-3 mb-4">
      {[['Invoices', summary.totalInvoices], ['Invoice value', currency.format(summary.invoiceValue)], ['Received', currency.format(summary.amountReceived)], ['Outstanding', currency.format(summary.amountPending)]].map(([label, value]) => <Col sm={6} xl={3} key={label}><Card className="h-100"><Card.Body><Card.Text className="text-muted mb-1">{label}</Card.Text><Card.Title className={label === 'Outstanding' && summary.amountPending > 0 ? 'text-danger' : ''}>{value}</Card.Title></Card.Body></Card></Col>)}
    </Row>}

    {!selectedCustomer ? <Alert variant="light" className="text-center">Select a customer to view credit transactions.</Alert> : transactionsLoading ? <div className="text-center p-5"><Spinner /><p className="text-muted mt-2">Loading transactions...</p></div> : transactions.length === 0 ? <Alert variant="light" className="text-center">No transactions available.</Alert> : <>
      <div className="table-responsive"><Table striped bordered hover><thead className="table-dark"><tr><th>Date</th><th>Invoice/reference</th><th className="text-end">Amount</th><th>Type</th><th>Remark</th></tr></thead><tbody>{transactions.map((transaction) => <tr key={transaction.trans_id} className={transaction.trans_type === "D" ? "table-danger" : transaction.trans_type === "C" ? "table-success" : ""}><td>{new Date(transaction.trans_date).toLocaleDateString("en-IN")}</td><td>{transaction.trans_id_pos || "—"}</td><td className="text-end">{currency.format(Number(transaction.trans_amount))}</td><td>{transaction.trans_type === "D" ? "Invoice" : transaction.trans_type === "C" ? "Payment" : transaction.trans_type}</td><td>{transaction.trans_remark || "—"}</td></tr>)}</tbody></Table></div>
      {totalPages > 1 && <Paginate currentPage={page} totalPages={totalPages} onPageChange={(nextPage) => fetchTransactions(selectedCustomer, nextPage)} />}
    </>}

    <Modal show={showReceiveModal} onHide={closeReceiveModal} backdrop="static" centered><Form onSubmit={submitPayment}><Modal.Header closeButton={busyAction !== "payment"}><Modal.Title>Receive payment</Modal.Title></Modal.Header><Modal.Body><Alert variant="info">Outstanding balance: <strong>{currency.format(summary.amountPending)}</strong></Alert><Form.Group className="mb-3"><Form.Label>Amount</Form.Label><Form.Control type="number" name="amount" min="0.01" step="0.01" max={summary.amountPending} value={receiveData.amount} onChange={(event) => setReceiveData((current) => ({ ...current, amount: event.target.value }))} required autoFocus /></Form.Group><Form.Group><Form.Label>Remark</Form.Label><Form.Control as="textarea" rows={3} maxLength={500} value={receiveData.remark} onChange={(event) => setReceiveData((current) => ({ ...current, remark: event.target.value }))} placeholder="Optional payment reference or note" /><Form.Text>{receiveData.remark.length}/500</Form.Text></Form.Group></Modal.Body><Modal.Footer><Button variant="secondary" onClick={closeReceiveModal} disabled={busyAction === "payment"}>Cancel</Button><Button variant="success" type="submit" disabled={busyAction === "payment"}>{busyAction === "payment" ? <><Spinner size="sm" className="me-2" />Recording...</> : "Record payment"}</Button></Modal.Footer></Form></Modal>
  </main>;
};

export default CustomerTransactions;
